import type {
  AdminAccount,
  SystemHealth,
  ReleaseStatus,
  AdminRedeemCode,
  AdminRedeemRedemption,
  HomeStatsSettings,
  StorageBackend,
  AdminContentModeration,
  AdminBonusRule,
  AdminCryptoDeposit,
  AdminCryptoNetwork,
  AdminDepositClaim,
  AdminLedgerEntry,
  AdminLoginResultSchema,
  AdminMe,
  AdminModel,
  AdminReferralCommission,
  AdminReferralSettlement,
  AdminReferrer,
  AdminReseller,
  AdminResellerCommission,
  AdminResellerSettlement,
  AdminTask,
  AdminTaskDetail,
  AdminUser,
  AdminUserDetail,
  Alert,
  AssetObject,
  AuditEvent,
  Channel,
  Dashboard,
  ProviderAccount,
  ModelRoutingSettings,
  QuotaPreview,
  ResellerCustomer,
  RoutingPreview,
  ReferralSettings,
  SiteSettings,
  TelegramSettings,
  TopupTermsSettings,
} from '@aitoken/contracts';
import { infiniteQueryOptions, queryOptions, type InfiniteData } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, type ListResponse, type Page } from './api';

export type AdminLoginResult = z.infer<typeof AdminLoginResultSchema>;

export interface SearchHit {
  type: 'user' | 'task' | 'asset' | 'ledger';
  id: string;
  label: string;
  sublabel: string | null;
}

export type AdminAsset = AssetObject & { user_email: string; user_id?: string };
export interface AssetReplica {
  project_name: string;
  provider_account_label: string;
  status: string;
  upstream_asset_id: string | null;
  last_error: string | null;
  updated_at: string;
}
export type AdminAssetDetail = AdminAsset & { replicas: AssetReplica[] };

export interface ChannelCheckResult {
  ok: boolean;
  latency_ms: number | null;
  message: string | null;
}

export const PAGE_SIZE = 20;

/* ---------------------------------------------------------------- keys & options */

export const meQuery = () =>
  queryOptions({
    queryKey: ['me'] as const,
    queryFn: ({ signal }) => api.get<AdminMe>('/admin/me', undefined, signal),
    staleTime: 5 * 60_000,
    retry: false,
  });

/** 系统健康：每 30 秒刷新（实例心跳也是 30 秒一次） */
export const systemHealthQuery = () =>
  queryOptions({
    queryKey: ['system-health'] as const,
    queryFn: ({ signal }) => api.get<SystemHealth>('/admin/system/health', undefined, signal),
    refetchInterval: 30_000,
  });

/** 发布：staging / production 版本与最近的发布任务（发布进行中时 15 秒刷新一次） */
export const releaseStatusQuery = () =>
  queryOptions({
    queryKey: ['release-status'] as const,
    queryFn: ({ signal }) => api.get<ReleaseStatus>('/admin/system/release', undefined, signal),
    refetchInterval: 15_000,
  });

export const dashboardQuery = () =>
  queryOptions({
    queryKey: ['dashboard'] as const,
    queryFn: ({ signal }) => api.get<Dashboard>('/admin/dashboard', undefined, signal),
    refetchInterval: 60_000,
  });

export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: ['search', q] as const,
    queryFn: ({ signal }) => api.get<ListResponse<SearchHit>>('/admin/search', { q }, signal),
    enabled: q.trim().length >= 2,
    staleTime: 15_000,
  });

function pageQuery<T, F extends Record<string, string | undefined>>(key: string, path: string, filters: F) {
  return infiniteQueryOptions<
    Page<T>,
    Error,
    InfiniteData<Page<T>, string | null>,
    readonly [string, F],
    string | null
  >({
    queryKey: [key, filters] as const,
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<T>>(path, { ...filters, limit: PAGE_SIZE, after: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : null),
  });
}

export interface UserFilters extends Record<string, string | undefined> {
  q?: string;
  status?: string;
}
export const usersQuery = (filters: UserFilters) =>
  pageQuery<AdminUser, UserFilters>('users', '/admin/users', filters);

export const userQuery = (id: string) =>
  queryOptions({
    queryKey: ['user', id] as const,
    queryFn: ({ signal }) =>
      api.get<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`, undefined, signal),
  });

export interface LedgerFilters extends Record<string, string | undefined> {
  user_id?: string;
  type?: string;
  reference_no?: string;
}
export const ledgerQuery = (filters: LedgerFilters) =>
  pageQuery<AdminLedgerEntry, LedgerFilters>('ledger', '/admin/ledger', filters);

export const modelsQuery = () =>
  queryOptions({
    queryKey: ['models'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<AdminModel>>('/admin/models', undefined, signal),
  });

export const providerAccountsQuery = () =>
  queryOptions({
    queryKey: ['provider-accounts'] as const,
    queryFn: ({ signal }) =>
      api.get<ListResponse<ProviderAccount>>('/admin/provider-accounts', undefined, signal),
  });

export const channelsQuery = () =>
  queryOptions({
    queryKey: ['channels'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<Channel>>('/admin/channels', undefined, signal),
  });

export const routingPreviewQuery = (model: string) =>
  queryOptions({
    queryKey: ['routing-preview', model] as const,
    queryFn: ({ signal }) => api.get<RoutingPreview>('/admin/routing-preview', { model }, signal),
    enabled: model.length > 0,
  });

export interface AssetFilters extends Record<string, string | undefined> {
  user_id?: string;
  q?: string;
}
export const assetsQuery = (filters: AssetFilters) =>
  pageQuery<AdminAsset, AssetFilters>('assets', '/admin/assets', filters);

export const assetQuery = (id: string) =>
  queryOptions({
    queryKey: ['asset', id] as const,
    queryFn: ({ signal }) =>
      api.get<AdminAssetDetail>(`/admin/assets/${encodeURIComponent(id)}`, undefined, signal),
  });

export interface TaskFilters extends Record<string, string | undefined> {
  status?: string;
  user_id?: string;
  failure_code?: string;
  q?: string;
}
export const tasksQuery = (filters: TaskFilters) =>
  pageQuery<AdminTask, TaskFilters>('tasks', '/admin/tasks', filters);

export const taskQuery = (id: string) =>
  queryOptions({
    queryKey: ['task', id] as const,
    queryFn: ({ signal }) =>
      api.get<AdminTaskDetail>(`/admin/tasks/${encodeURIComponent(id)}`, undefined, signal),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && status !== 'succeeded' && status !== 'failed' ? 10_000 : false;
    },
  });

export const alertsQuery = (status: string | undefined) =>
  queryOptions({
    queryKey: ['alerts', status ?? 'all'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<Alert>>('/admin/alerts', { status }, signal),
  });

/** 顶栏铃铛：待处理告警数 */
export const openAlertsQuery = () =>
  queryOptions({
    ...alertsQuery('open'),
    refetchInterval: 60_000,
    select: (res: ListResponse<Alert>) => res.data.length,
  });

export interface AuditFilters extends Record<string, string | undefined> {
  action?: string;
}
export const auditQuery = (filters: AuditFilters) =>
  pageQuery<AuditEvent, AuditFilters>('audit', '/admin/audit-events', filters);

export const adminsQuery = () =>
  queryOptions({
    queryKey: ['admins'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<AdminAccount>>('/admin/admins', undefined, signal),
  });

export const settingsQuery = () =>
  queryOptions({
    queryKey: ['settings'] as const,
    queryFn: ({ signal }) => api.get<SiteSettings>('/admin/settings', undefined, signal),
  });

/** 已配置、可写入新文件的存储后端 */
export const storageBackendsQuery = () =>
  queryOptions({
    queryKey: ['settings', 'storage'] as const,
    queryFn: ({ signal }) => api.get<{ available: StorageBackend[]; default_backend: StorageBackend }>('/admin/settings/storage', undefined, signal),
    staleTime: 60_000,
  });

/** 首页成功率展示设置与预览 */
export const homeStatsQuery = () =>
  queryOptions({
    queryKey: ['settings', 'home-stats'] as const,
    queryFn: ({ signal }) => api.get<HomeStatsView>('/admin/settings/home-stats', undefined, signal),
  });

export interface HomeStatsView {
  settings: HomeStatsSettings;
  preview: { model: string; display_name: string; success_rate: number; source: 'measured' | 'configured' }[];
}

export type { QuotaPreview };

/* ---------------------------------------------------------------- 推荐分润 / Telegram（docs/11） */

export interface ReferrerFilters extends Record<string, string | undefined> {
  q?: string;
  pending?: string;
}
export const referrersQuery = (filters: ReferrerFilters) =>
  pageQuery<AdminReferrer, ReferrerFilters>('referrers', '/admin/referrals/referrers', filters);

export interface CommissionFilters extends Record<string, string | undefined> {
  referrer_id?: string;
  referee_id?: string;
  status?: string;
  settlement_id?: string;
}
export const commissionsQuery = (filters: CommissionFilters) =>
  pageQuery<AdminReferralCommission, CommissionFilters>('referral-commissions', '/admin/referrals/commissions', filters);

export interface SettlementFilters extends Record<string, string | undefined> {
  referrer_id?: string;
}
export const referralSettlementsQuery = (filters: SettlementFilters) =>
  pageQuery<AdminReferralSettlement, SettlementFilters>('referral-settlements', '/admin/referrals/settlements', filters);

/** 单个推荐人的汇总：按用户 ID 精确检索推荐人列表 */
export const referrerQuery = (userId: string) =>
  queryOptions({
    queryKey: ['referrer', userId] as const,
    queryFn: ({ signal }) =>
      api
        .get<Page<AdminReferrer>>('/admin/referrals/referrers', { q: userId, limit: 1 }, signal)
        .then((p) => p.data[0] ?? null),
  });

export const referralSettingsQuery = () =>
  queryOptions({
    queryKey: ['settings', 'referral'] as const,
    queryFn: ({ signal }) => api.get<ReferralSettings>('/admin/settings/referral', undefined, signal),
  });

export const legalDocQuery = (doc: 'terms' | 'privacy') =>
  queryOptions({
    queryKey: ['settings', 'legal', doc] as const,
    queryFn: ({ signal }) => api.get<TopupTermsSettings>(`/admin/settings/legal/${doc}`, undefined, signal),
  });

export const contentModerationQuery = () =>
  queryOptions({
    queryKey: ['settings', 'content-moderation'] as const,
    queryFn: ({ signal }) => api.get<AdminContentModeration>('/admin/settings/content-moderation', undefined, signal),
  });

export const telegramSettingsQuery = () =>
  queryOptions({
    queryKey: ['settings', 'telegram'] as const,
    queryFn: ({ signal }) => api.get<TelegramSettings>('/admin/settings/telegram', undefined, signal),
  });

/* ---------------------------------------------------------------- 分销渠道 / 模型路由（docs/12） */

export interface ResellerFilters extends Record<string, string | undefined> {
  q?: string;
  status?: string;
}
export const resellersQuery = (filters: ResellerFilters) =>
  queryOptions({
    queryKey: ['resellers', filters] as const,
    queryFn: ({ signal }) => api.get<ListResponse<AdminReseller>>('/admin/resellers', filters, signal),
  });

export const resellerQuery = (id: string) =>
  queryOptions({
    queryKey: ['reseller', id] as const,
    queryFn: ({ signal }) => api.get<AdminReseller>(`/admin/resellers/${encodeURIComponent(id)}`, undefined, signal),
  });

/** reseller_id 只用于区分缓存（接口按路径取渠道，忽略该参数） */
export interface ResellerCustomerFilters extends Record<string, string | undefined> {
  reseller_id: string;
  q?: string;
}
export const resellerCustomersQuery = (filters: ResellerCustomerFilters) =>
  pageQuery<ResellerCustomer, ResellerCustomerFilters>(
    'reseller-customers',
    `/admin/resellers/${encodeURIComponent(filters.reseller_id)}/customers`,
    filters,
  );

export interface ResellerCommissionFilters extends Record<string, string | undefined> {
  reseller_id?: string;
  customer_id?: string;
  status?: string;
  settlement_id?: string;
  month?: string;
}
export const resellerCommissionsQuery = (filters: ResellerCommissionFilters) =>
  pageQuery<AdminResellerCommission, ResellerCommissionFilters>('reseller-commissions', '/admin/reseller-commissions', filters);

export interface ResellerSettlementFilters extends Record<string, string | undefined> {
  reseller_id?: string;
}
export const resellerSettlementsQuery = (filters: ResellerSettlementFilters) =>
  pageQuery<AdminResellerSettlement, ResellerSettlementFilters>('reseller-settlements', '/admin/reseller-settlements', filters);

export const modelRoutingQuery = () =>
  queryOptions({
    queryKey: ['settings', 'model-routing'] as const,
    queryFn: ({ signal }) => api.get<ModelRoutingSettings>('/admin/settings/model-routing', undefined, signal),
  });

/* ---------------------------------------------------------------- 在线充值（docs/14，仅海外站） */

export interface CryptoDepositFilters extends Record<string, string | undefined> {
  status?: string;
  chain?: string;
  user_id?: string;
  q?: string;
}
export const cryptoDepositsQuery = (filters: CryptoDepositFilters) =>
  pageQuery<AdminCryptoDeposit, CryptoDepositFilters>('crypto-deposits', '/admin/crypto-deposits', filters);

export interface RedeemFilters extends Record<string, string | undefined> {
  q?: string;
  state?: string;
  batch_no?: string;
}
export const redeemCodesQuery = (filters: RedeemFilters) =>
  pageQuery<AdminRedeemCode, RedeemFilters>('redeem-codes', '/admin/redeem-codes', filters);

export const redeemCodeQuery = (id: string) =>
  queryOptions({
    queryKey: ['redeem-code', id] as const,
    queryFn: ({ signal }) =>
      api.get<{ code: AdminRedeemCode; redemptions: AdminRedeemRedemption[] }>(`/admin/redeem-codes/${encodeURIComponent(id)}`, undefined, signal),
  });

export const cryptoDepositQuery = (id: string) =>
  queryOptions({
    queryKey: ['crypto-deposit', id] as const,
    queryFn: ({ signal }) =>
      api.get<AdminCryptoDeposit>(`/admin/crypto-deposits/${encodeURIComponent(id)}`, undefined, signal),
  });

export const cryptoNetworksQuery = () =>
  queryOptions({
    queryKey: ['crypto-networks'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<AdminCryptoNetwork>>('/admin/crypto-networks', undefined, signal),
  });

export const cryptoClaimsQuery = (status: string | undefined) =>
  queryOptions({
    queryKey: ['crypto-claims', status ?? 'all'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<AdminDepositClaim>>('/admin/crypto-claims', { status }, signal),
  });

export const bonusRulesQuery = () =>
  queryOptions({
    queryKey: ['topup-bonus-rules'] as const,
    queryFn: ({ signal }) => api.get<ListResponse<AdminBonusRule>>('/admin/topup-bonus-rules', undefined, signal),
  });

export const cryptoTopupSettingsQuery = () =>
  queryOptions({
    queryKey: ['settings', 'crypto-topup'] as const,
    queryFn: ({ signal }) =>
      api.get<{ risk_hold_threshold: string }>('/admin/settings/crypto-topup', undefined, signal),
  });

export const topupTermsQuery = () =>
  queryOptions({
    queryKey: ['settings', 'topup-terms'] as const,
    queryFn: ({ signal }) => api.get<TopupTermsSettings>('/admin/settings/topup-terms', undefined, signal),
  });
